import { useMemo, useState, type ReactNode } from "react"
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import { CaretDown, CaretRight, Folder, FolderPlus, Package, Plus } from "@phosphor-icons/react"

import { Badge, Button, Input } from "~/components/design-system/components"
import { usePermission } from "~/hooks/usePermission"
import { PACKAGE_NAME_MAX, packageName } from "~/utils/allocationPackageOps"
import { cn } from "~/utils/helpers"
import { PACKAGE_STATUS_META } from "./packageDisplay"
import { PackageIssueBadge } from "./PackageIssueBadge"
import { usePackageStoresDraft } from "./setup/setupDrafts"
import type { PackageFolder, RangePackage } from "./types"

export type PackageSelection = { kind: "package" | "folder"; id: string } | null

const ROOT = "root"

type DragItem = { kind: "package" | "folder"; id: string }

const dragId = (item: DragItem) => `${item.kind}:${item.id}`
const parseDragId = (value: string): DragItem | null => {
  const [kind, ...rest] = value.split(":")
  if (kind !== "package" && kind !== "folder") return null
  return { kind, id: rest.join(":") }
}

/** Where a drop lands: a package into any folder or the root; a folder into a top-level folder or the root. */
function dropTarget(
  item: DragItem,
  overId: string,
  folders: PackageFolder[],
): { folderId: string | null } | null {
  const target = overId === ROOT ? null : folders.find((folder) => folder.id === overId)
  if (overId !== ROOT && !target) return null
  if (item.kind === "package") return { folderId: target?.id ?? null }
  if (target === undefined || target === null) return { folderId: null }
  if (target.id === item.id || target.parentId !== null) return null
  if (folders.some((folder) => folder.parentId === item.id)) return null
  return { folderId: target.id }
}

function DropZone({ id, children }: { id: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div ref={setNodeRef} className={cn("rounded-md", isOver && "bg-subtle-hover")}>
      {children}
    </div>
  )
}

function Draggable({
  item,
  disabled,
  children,
}: {
  item: DragItem
  disabled: boolean
  children: ReactNode
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(item),
    disabled,
  })
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={cn(isDragging && "opacity-50")}>
      {children}
    </div>
  )
}

function Row({
  depth,
  selected,
  onClick,
  children,
}: {
  depth: number
  selected: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button
      variant="transparent"
      size="sm"
      onClick={onClick}
      wrapperClassName="w-full"
      className={cn(
        "w-full justify-start gap-2 rounded-none [&>div]:w-full [&>div]:justify-start [&>div]:gap-2",
        depth === 1 && "pl-6",
        depth === 2 && "pl-10",
        selected && "bg-base-pressed",
      )}
    >
      {children}
    </Button>
  )
}

function PackageRow({
  pkg,
  depth,
  selection,
  canDrag,
  deleting,
  onSelect,
}: {
  pkg: RangePackage
  depth: number
  selection: PackageSelection
  canDrag: boolean
  deleting: boolean
  onSelect: (selection: PackageSelection) => void
}) {
  const status = PACKAGE_STATUS_META[pkg.status]
  return (
    <Draggable item={{ kind: "package", id: pkg.id }} disabled={!canDrag}>
      <Row
        depth={depth}
        selected={selection?.kind === "package" && selection.id === pkg.id}
        onClick={() => onSelect({ kind: "package", id: pkg.id })}
      >
        <Package size={14} className="shrink-0 text-fg-muted" />
        <span className={cn("flex-1 truncate text-left", deleting && "text-fg-muted line-through")}>
          {pkg.name}
        </span>
        {deleting && <span className="paragraph-xs text-fg-muted">· deleting on save</span>}
        <PackageIssueBadge pkg={pkg} />
        <Badge variant={status.variant}>{status.label}</Badge>
      </Row>
    </Draggable>
  )
}

function FolderRow({
  folder,
  depth,
  open,
  count,
  selection,
  canDrag,
  deleting,
  onToggle,
  onSelect,
}: {
  folder: PackageFolder
  depth: number
  open: boolean
  count: number
  selection: PackageSelection
  canDrag: boolean
  deleting: boolean
  onToggle: () => void
  onSelect: (selection: PackageSelection) => void
}) {
  const Caret = open ? CaretDown : CaretRight
  return (
    <Draggable item={{ kind: "folder", id: folder.id }} disabled={!canDrag}>
      <div className="flex items-center">
        <Button
          variant="transparent"
          size="sm"
          aria-label={open ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          onClick={onToggle}
          className={cn("shrink-0 px-1", depth === 1 && "ml-4")}
        >
          <Caret size={12} />
        </Button>
        <div className="min-w-0 flex-1">
          <Row
            depth={0}
            selected={selection?.kind === "folder" && selection.id === folder.id}
            onClick={() => onSelect({ kind: "folder", id: folder.id })}
          >
            <Folder size={14} weight="fill" className="shrink-0 text-fg-muted" />
            <span
              className={cn(
                "flex-1 truncate text-left font-medium",
                deleting && "text-fg-muted line-through",
              )}
            >
              {folder.name}
            </span>
            {deleting && <span className="paragraph-xs text-fg-muted">· deleting on save</span>}
            <span className="paragraph-xs text-fg-muted tabular-nums">{count}</span>
          </Row>
        </div>
      </div>
    </Draggable>
  )
}

type Tree = {
  folders: PackageFolder[]
  packages: RangePackage[]
  childrenOf: (folderId: string) => PackageFolder[]
  packagesIn: (folderId: string) => RangePackage[]
  countIn: (folderId: string) => number
}

function FolderBranch({
  folder,
  tree,
  selection,
  canDrag,
  isDeleting,
  isFolderDeleting,
  collapsed,
  onToggle,
  onSelect,
}: {
  folder: PackageFolder
  tree: Tree
  selection: PackageSelection
  canDrag: boolean
  isDeleting: (packageId: string) => boolean
  isFolderDeleting: (folderId: string) => boolean
  collapsed: Set<string>
  onToggle: (folderId: string) => void
  onSelect: (selection: PackageSelection) => void
}) {
  const depth = folder.parentId === null ? 0 : 1
  const open = !collapsed.has(folder.id)
  return (
    <div>
      <DropZone id={folder.id}>
        <FolderRow
          folder={folder}
          depth={depth}
          open={open}
          count={tree.countIn(folder.id)}
          selection={selection}
          canDrag={canDrag}
          deleting={isFolderDeleting(folder.id)}
          onToggle={() => onToggle(folder.id)}
          onSelect={onSelect}
        />
      </DropZone>
      {open && (
        <>
          {tree.childrenOf(folder.id).map((child) => (
            <FolderBranch
              key={child.id}
              folder={child}
              tree={tree}
              selection={selection}
              canDrag={canDrag}
              isDeleting={isDeleting}
              isFolderDeleting={isFolderDeleting}
              collapsed={collapsed}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
          {tree.packagesIn(folder.id).map((pkg) => (
            <PackageRow
              key={pkg.id}
              pkg={pkg}
              depth={depth + 1}
              selection={selection}
              canDrag={canDrag}
              deleting={isDeleting(pkg.id)}
              onSelect={onSelect}
            />
          ))}
        </>
      )}
    </div>
  )
}

/**
 * Splits the tree into the active section and the Archived one: archived
 * packages, and archived folders with everything in them, go to Archived. An
 * archived package in an active folder shows on its own there.
 */
function buildTree(
  packages: RangePackage[],
  folders: PackageFolder[],
  archived: boolean,
): Tree & { roots: PackageFolder[]; loose: RangePackage[] } {
  const archivedFolderIds = new Set(
    folders.filter((folder) => folder.deactivated).map((folder) => folder.id),
  )
  const visiblePackages = packages.filter((pkg) => {
    const isArchived =
      pkg.ownDeactivated || (pkg.folderId !== null && archivedFolderIds.has(pkg.folderId))
    return isArchived === archived
  })
  const visibleFolders = folders.filter((folder) => folder.deactivated === archived)
  const visibleIds = new Set(visibleFolders.map((folder) => folder.id))
  const packagesByFolder = Map.groupBy(visiblePackages, (pkg) => pkg.folderId)
  const childrenByParent = Map.groupBy(visibleFolders, (folder) => folder.parentId)
  const loose = visiblePackages.filter(
    (pkg) => pkg.folderId === null || !visibleIds.has(pkg.folderId),
  )
  const roots = visibleFolders.filter(
    (folder) => folder.parentId === null || !visibleIds.has(folder.parentId),
  )
  const childrenOf = (folderId: string) => childrenByParent.get(folderId) ?? []
  const packagesIn = (folderId: string) => packagesByFolder.get(folderId) ?? []
  const countIn = (folderId: string) =>
    packagesIn(folderId).length +
    childrenOf(folderId).reduce((sum, child) => sum + packagesIn(child.id).length, 0)
  return {
    folders: visibleFolders,
    packages: visiblePackages,
    roots,
    loose,
    childrenOf,
    packagesIn,
    countIn,
  }
}

/** The name-only step of New folder: Enter creates an empty active folder, Esc cancels. */
function NewFolderName({
  siblings,
  onCreate,
  onCancel,
}: {
  siblings: PackageFolder[]
  onCreate: (name: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState("")
  const taken = new Set(siblings.map((folder) => folder.name.trim().toLowerCase()))
  const error = newFolderNameError(value, taken)
  return (
    <div className="flex flex-col gap-1">
      <Input
        autoFocus
        aria-label="New folder name"
        placeholder="Folder name, e.g. AW26"
        value={value}
        maxLength={PACKAGE_NAME_MAX}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && value.trim() !== "" && !error) onCreate(value.trim())
          if (event.key === "Escape") onCancel()
        }}
        onBlur={() => {
          if (value.trim() === "") onCancel()
        }}
      />
      <p className={cn("paragraph-xs", error ? "text-fg-negative-text" : "text-fg-muted")}>
        {error ?? "Press Enter to create the folder, Esc to cancel."}
      </p>
    </div>
  )
}

function newFolderNameError(value: string, taken: Set<string>) {
  if (value.trim() === "") return undefined
  const parsed = packageName.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message.replace("package", "folder")
  if (taken.has(value.trim().toLowerCase())) return "Another folder already carries this name."
  return undefined
}

export function PackageFolderTree({
  packages,
  folders,
  selection,
  onSelect,
  onNewPackage,
  onNewFolder,
  onMovePackage,
  onMoveFolder,
}: {
  packages: RangePackage[]
  folders: PackageFolder[]
  selection: PackageSelection
  onSelect: (selection: PackageSelection) => void
  onNewPackage: () => void
  onNewFolder: (name: string) => void
  onMovePackage: (packageId: string, folderId: string | null) => void
  onMoveFolder: (folderId: string, parentId: string | null) => void
}) {
  const { allowed } = usePermission("edit")
  const { isDeleting, isFolderDeleting } = usePackageStoresDraft()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [naming, setNaming] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const active = useMemo(() => buildTree(packages, folders, false), [packages, folders])
  const archived = useMemo(() => buildTree(packages, folders, true), [packages, folders])
  const archivedCount = archived.roots.length + archived.loose.length
  const toggle = (folderId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })

  const onDragEnd = ({ active: dragged, over }: DragEndEvent) => {
    if (!over) return
    const item = parseDragId(String(dragged.id))
    if (!item) return
    const target = dropTarget(item, String(over.id), folders)
    if (!target) return
    if (item.kind === "package") {
      const pkg = packages.find((other) => other.id === item.id)
      if (pkg && pkg.folderId !== target.folderId) onMovePackage(item.id, target.folderId)
      return
    }
    const folder = folders.find((other) => other.id === item.id)
    if (folder && folder.parentId !== target.folderId) onMoveFolder(item.id, target.folderId)
  }

  const branchProps = {
    selection,
    canDrag: allowed,
    isDeleting,
    isFolderDeleting,
    collapsed,
    onToggle: toggle,
    onSelect,
  }

  return (
    <div className="flex w-[300px] shrink-0 flex-col border-r border-border-base">
      {naming && (
        <div className="border-b border-border-base p-3">
          <NewFolderName
            siblings={folders.filter((folder) => folder.parentId === null)}
            onCreate={(name) => {
              setNaming(false)
              onNewFolder(name)
            }}
            onCancel={() => setNaming(false)}
          />
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex flex-1 flex-col overflow-y-auto py-1">
          {active.roots.map((folder) => (
            <FolderBranch key={folder.id} folder={folder} tree={active} {...branchProps} />
          ))}
          <DropZone id={ROOT}>
            {active.loose.map((pkg) => (
              <PackageRow
                key={pkg.id}
                pkg={pkg}
                depth={0}
                selection={selection}
                canDrag={allowed}
                deleting={isDeleting(pkg.id)}
                onSelect={onSelect}
              />
            ))}
            <div className="h-6" />
          </DropZone>
          {active.roots.length === 0 && active.loose.length === 0 && (
            <p className="p-4 text-center text-sm text-fg-subtle">
              {folders.length + packages.length === 0
                ? "No folders yet. A folder holds the packages for one selling period, like AW26."
                : "Nothing here yet"}
            </p>
          )}
          {archivedCount > 0 && (
            <div className="mt-2 border-t border-border-base pt-1">
              <Button
                variant="transparent"
                size="sm"
                className="w-full justify-start gap-1"
                wrapperClassName="w-full"
                onClick={() => setArchivedOpen(!archivedOpen)}
              >
                {archivedOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                Archived
                <span className="paragraph-xs text-fg-muted tabular-nums">{archivedCount}</span>
              </Button>
              {archivedOpen && (
                <>
                  {archived.roots.map((folder) => (
                    <FolderBranch
                      key={folder.id}
                      folder={folder}
                      tree={archived}
                      {...branchProps}
                      canDrag={false}
                    />
                  ))}
                  {archived.loose.map((pkg) => (
                    <PackageRow
                      key={pkg.id}
                      pkg={pkg}
                      depth={0}
                      selection={selection}
                      canDrag={false}
                      deleting={isDeleting(pkg.id)}
                      onSelect={onSelect}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </DndContext>

      <div className="flex gap-2 border-t border-border-base p-3">
        <Button
          variant="neutral"
          className="w-full gap-1.5"
          wrapperClassName="flex-1"
          size="sm"
          requires="edit"
          onClick={() => setNaming(true)}
        >
          <FolderPlus size={14} />
          New folder
        </Button>
        <Button
          variant="neutral"
          className="w-full gap-1.5"
          wrapperClassName="flex-1"
          size="sm"
          requires="edit"
          onClick={onNewPackage}
        >
          <Plus size={14} />
          New package
        </Button>
      </div>
    </div>
  )
}
